// tracker: songs for a snes game. each person keeps their own. a song is notes in patterns plus the sounds they play,
// all in one json file. the server and the app clean a song with the same code, and the same checks decide what the
// limits bar shows and whether the game file can be exported, so what is saved and what is exported always agree.
//
// the limits come from pvsneslib's music docs (smconv): impulse tracker only, instrument mode with one sample per
// instrument, 8 voices, 64 samples that fit in 58k after brr compression, no note above 128 khz, loops on 16 sample steps

export type TrackerMode = "snes" | "free";

export const NOTE_CUT = -1;
export const MAX_NOTE = 119;
export const IT_C5 = 60;
export const MAX_SONGS = 100;
export const MAX_PATTERNS = 200;
export const MAX_ORDER = 255;
export const ROW_CHOICES = [16, 32, 48, 64];
export const MAX_SOUND_SAMPLES = 400_000;
export const MAX_SONG_BYTES = 2_500_000;
export const BRR_BLOCK = 16;
export const MAX_PLAY_RATE = 128_000;
export const RATE_CHOICES = [8000, 11025, 16000, 22050, 32000];
export const SOUND_NAME_MAX = 24;
export const TITLE_MAX = 40;

// what a song may hold while editing. free mode lets it grow past what the snes takes, the checks then warn
export const LIMITS: Record<TrackerMode, { voices: number; sounds: number }> = {
  snes: { voices: 8, sounds: 64 },
  free: { voices: 16, sounds: 99 }
};
// what the snes takes. kb here is 1000 bytes: the docs say "58k" without saying which, and the smaller one is the safe one
export const SNES_LIMITS = { voices: 8, sounds: 64, bytes: 58_000 };
export const SNES_BYTES = SNES_LIMITS.bytes;
// the memory bar turns yellow from here
export const WARN_AT = 0.8;
// pvsneslib plays game sound effects on voices 7 and 8
export const SFX_VOICES = 2;

// the only effects a cell can carry, and the only ones the exporter writes. one list so a new one is a single line.
// the pvsneslib docs do not say which effects smconv understands, so this stays small until a friend confirms more
export interface SafeEffect {
  id: string;
  // the impulse tracker command number
  it: number;
  label: string;
  detail: string;
  min: number;
  max: number;
  start: number;
}

export const SAFE_EFFECTS: SafeEffect[] = [
  { id: "A", it: 1, label: "speed", detail: "ticks per row, lower is faster", min: 1, max: 31, start: 6 },
  { id: "T", it: 20, label: "tempo", detail: "beats per minute, 32 to 255", min: 32, max: 255, start: 125 },
  { id: "B", it: 2, label: "go to song position", detail: "jump to a place in the song order", min: 0, max: 254, start: 0 },
  { id: "C", it: 3, label: "next pattern", detail: "stop this pattern early, start the next at this row", min: 0, max: 63, start: 0 }
];

export function effectById(id: string | undefined): SafeEffect | undefined {
  return SAFE_EFFECTS.find((effect) => effect.id === id);
}

export interface TrackerSound {
  id: string;
  name: string;
  // recording speed in hz, also what the C-5 key plays at
  rate: number;
  // signed 8 bit mono samples as base64
  data: string;
  // the part that is kept, in samples of data
  trimStart: number;
  trimEnd: number;
  loop: boolean;
  loopStart: number;
  loopEnd: number;
  // 0 to 64
  volume: number;
}

export interface TrackerNote {
  // row and voice, both from 0
  r: number;
  c: number;
  // 0 to 119 (C-0 up), or NOTE_CUT
  n: number;
  // index in song.sounds
  s: number;
  // volume 0 to 64, -1 leaves the sound's own
  v: number;
  fx?: string;
  fp?: number;
}

export interface TrackerPattern {
  rows: number;
  notes: TrackerNote[];
}

export interface TrackerSong {
  id: string;
  title: string;
  mode: TrackerMode;
  speed: number;
  tempo: number;
  // pattern numbers in play order
  order: number[];
  patterns: TrackerPattern[];
  sounds: TrackerSound[];
  // keep voices 7 and 8 empty for the game's own sound effects
  sfx: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TrackerSummary {
  id: string;
  title: string;
  mode: TrackerMode;
  updatedAt: string;
  voices: number;
  sounds: number;
  bytes: number;
  blocked: boolean;
}

const ID = /^[a-z0-9]{4,24}$/i;
const B64 = /^[A-Za-z0-9+/]*={0,2}$/;

export function newTrackerId(): string {
  let out = "";
  for (let i = 0; i < 12; i++) out += Math.floor(Math.random() * 16).toString(16);
  return out;
}

export const isTrackerId = (id: string): boolean => ID.test(id);

// --- samples as base64

const sampleCache = new Map<string, Int8Array>();

export function decodeSamples(data: string): Int8Array {
  const cached = sampleCache.get(data);
  if (cached) return cached;
  const text = atob(data);
  const out = new Int8Array(text.length);
  for (let i = 0; i < text.length; i++) out[i] = text.charCodeAt(i) << 24 >> 24;
  if (sampleCache.size > 80) sampleCache.clear();
  sampleCache.set(data, out);
  return out;
}

export function encodeSamples(samples: Int8Array): string {
  let text = "";
  for (let i = 0; i < samples.length; i += 8192) {
    text += String.fromCharCode(...Array.from(samples.subarray(i, i + 8192), (value) => value & 255));
  }
  return btoa(text);
}

// --- sounds as the file will have them

export interface FinalSound {
  data: Int8Array;
  loop: boolean;
  loopStart: number;
  loopEnd: number;
  rate: number;
  volume: number;
}

const clamp = (value: number, low: number, high: number): number => Math.min(high, Math.max(low, value));
const whole = (value: unknown, fallback: number): number => {
  const number = Math.round(Number(value));
  return Number.isFinite(number) ? number : fallback;
};

// trims the sound, snaps the loop to 16 sample steps and pads to whole brr blocks, which is what the snes needs
export function finalizeSound(sound: TrackerSound): FinalSound {
  const raw = decodeSamples(sound.data);
  const start = clamp(sound.trimStart, 0, raw.length);
  const end = sound.trimEnd > start && sound.trimEnd <= raw.length ? sound.trimEnd : raw.length;
  const part = raw.slice(start, end);
  const base = { rate: sound.rate, volume: sound.volume };
  if (!part.length) return { ...base, data: new Int8Array(0), loop: false, loopStart: 0, loopEnd: 0 };
  if (sound.loop) {
    const loopStart = Math.floor(clamp(sound.loopStart - start, 0, part.length - 1) / BRR_BLOCK) * BRR_BLOCK;
    const wanted = clamp(sound.loopEnd - start, loopStart + 1, part.length);
    let length = Math.max(BRR_BLOCK, Math.floor((wanted - loopStart) / BRR_BLOCK) * BRR_BLOCK);
    if (loopStart + length > part.length) length = Math.max(BRR_BLOCK, Math.floor((part.length - loopStart) / BRR_BLOCK) * BRR_BLOCK);
    const data = new Int8Array(loopStart + length);
    data.set(part.subarray(0, Math.min(part.length, data.length)));
    return { ...base, data, loop: true, loopStart, loopEnd: loopStart + length };
  }
  const data = new Int8Array(Math.ceil(part.length / BRR_BLOCK) * BRR_BLOCK);
  data.set(part);
  return { ...base, data, loop: false, loopStart: 0, loopEnd: 0 };
}

export const brrBytes = (samples: number): number => (samples / BRR_BLOCK) * 9;

export function soundBytes(sound: TrackerSound): number {
  return brrBytes(finalizeSound(sound).data.length);
}

// the highest key that keeps the sound under the 128 khz playback limit
export function maxNoteFor(rate: number): number {
  return clamp(Math.floor(IT_C5 + 12 * Math.log2(MAX_PLAY_RATE / Math.max(1, rate))), 0, MAX_NOTE);
}

export const noteName = (n: number): string => (n === NOTE_CUT ? "OFF" : `${["C-", "C#", "D-", "D#", "E-", "F-", "F#", "G-", "G#", "A-", "A#", "B-"][n % 12]}${Math.floor(n / 12)}`);

// --- cleaning

function cleanSound(raw: unknown): TrackerSound | null {
  const given = raw && typeof raw === "object" ? raw as Partial<TrackerSound> : {};
  const data = typeof given.data === "string" && B64.test(given.data) ? given.data : "";
  if (!data) return null;
  let length = 0;
  try {
    length = decodeSamples(data).length;
  } catch {
    return null;
  }
  if (!length || length > MAX_SOUND_SAMPLES) return null;
  const trimStart = clamp(whole(given.trimStart, 0), 0, length - 1);
  const trimEnd = clamp(whole(given.trimEnd, length), trimStart + 1, length);
  const loopStart = clamp(whole(given.loopStart, trimStart), 0, length - 1);
  return {
    id: ID.test(String(given.id || "")) ? String(given.id) : newTrackerId(),
    name: String(given.name || "sound").replace(/\s+/g, " ").trim().slice(0, SOUND_NAME_MAX) || "sound",
    rate: clamp(whole(given.rate, 16000), 2000, 48000),
    data,
    trimStart,
    trimEnd,
    loop: Boolean(given.loop),
    loopStart,
    loopEnd: clamp(whole(given.loopEnd, trimEnd), loopStart + 1, length),
    volume: clamp(whole(given.volume, 64), 0, 64)
  };
}

function cleanNote(raw: unknown, rows: number, voices: number): TrackerNote | null {
  const given = raw && typeof raw === "object" ? raw as Partial<TrackerNote> : {};
  const r = whole(given.r, -1);
  const c = whole(given.c, -1);
  const n = whole(given.n, -2);
  if (r < 0 || r >= rows || c < 0 || c >= voices || (n !== NOTE_CUT && (n < 0 || n > MAX_NOTE))) return null;
  const note: TrackerNote = { r, c, n, s: clamp(whole(given.s, 0), 0, 255), v: clamp(whole(given.v, -1), -1, 64) };
  const effect = effectById(String(given.fx || ""));
  if (effect) {
    note.fx = effect.id;
    note.fp = clamp(whole(given.fp, effect.start), effect.min, effect.max);
  }
  return note;
}

export function cleanSong(raw: unknown, now: string, keep: { id: string; createdAt?: string }): TrackerSong {
  const given = raw && typeof raw === "object" ? raw as Partial<TrackerSong> : {};
  const mode: TrackerMode = given.mode === "free" ? "free" : "snes";
  const limit = LIMITS[mode];
  const sounds: TrackerSound[] = [];
  const seen = new Set<string>();
  // a sound that cannot be read keeps its place as an empty one, so the notes after it do not slide onto another sound
  const slotOf: number[] = [];
  for (const item of Array.isArray(given.sounds) ? given.sounds.slice(0, limit.sounds + 40) : []) {
    const sound = cleanSound(item);
    if (!sound) {
      slotOf.push(-1);
      continue;
    }
    while (seen.has(sound.id)) sound.id = newTrackerId();
    seen.add(sound.id);
    slotOf.push(sounds.length);
    sounds.push(sound);
  }
  const patterns = (Array.isArray(given.patterns) ? given.patterns : []).slice(0, MAX_PATTERNS).map((item) => {
    const source = item && typeof item === "object" ? item as Partial<TrackerPattern> : {};
    const rows = ROW_CHOICES.includes(whole(source.rows, 32)) ? whole(source.rows, 32) : 32;
    const taken = new Set<string>();
    const notes: TrackerNote[] = [];
    for (const entry of Array.isArray(source.notes) ? source.notes.slice(0, 64 * 16) : []) {
      const note = cleanNote(entry, rows, limit.voices);
      if (!note || taken.has(`${note.r}:${note.c}`)) continue;
      // notes keep pointing at the sound they named even when an earlier one was dropped
      if (note.n !== NOTE_CUT) {
        const slot = slotOf.length ? slotOf[note.s] : -1;
        if (slot === undefined || slot < 0) continue;
        note.s = slot;
      } else {
        note.s = 0;
      }
      taken.add(`${note.r}:${note.c}`);
      notes.push(note);
    }
    notes.sort((a, b) => a.r - b.r || a.c - b.c);
    return { rows, notes };
  });
  if (!patterns.length) patterns.push({ rows: 32, notes: [] });
  const order = (Array.isArray(given.order) ? given.order : []).slice(0, MAX_ORDER).map((value) => whole(value, -1)).filter((value) => value >= 0 && value < patterns.length);
  if (!order.length) order.push(0);
  return {
    id: keep.id,
    title: String(given.title || "").replace(/\s+/g, " ").trim().slice(0, TITLE_MAX) || "new song",
    mode,
    speed: clamp(whole(given.speed, 6), 1, 31),
    tempo: clamp(whole(given.tempo, 125), 32, 255),
    order,
    patterns,
    sounds,
    sfx: Boolean(given.sfx),
    createdAt: keep.createdAt || now,
    updatedAt: now
  };
}

export function songBytes(song: TrackerSong): number {
  return JSON.stringify(song).length;
}

// --- the limits and the checks

export type MeterState = "ok" | "warn" | "bad";

export interface Check {
  id: string;
  state: MeterState;
  title: string;
  detail: string;
  fix: string;
  // true when the file would be wrong whatever the mode, so export stays locked
  fatal?: boolean;
}

export interface Report {
  mode: TrackerMode;
  voices: number;
  voiceMax: number;
  sounds: number;
  soundMax: number;
  bytes: number;
  byteMax: number;
  notes: number;
  checks: Check[];
  blocked: boolean;
}

// the voices the song may use on a real snes, whichever mode it is in
export const voiceLimit = (song: Pick<TrackerSong, "sfx">): number => SNES_LIMITS.voices - (song.sfx ? SFX_VOICES : 0);

export function meterState(value: number, max: number, mode: TrackerMode, soft = WARN_AT): MeterState {
  if (value > max) return mode === "snes" ? "bad" : "warn";
  return value > max * soft ? "warn" : "ok";
}

const kb = (bytes: number): string => (bytes / 1000).toFixed(1);

export function analyse(song: TrackerSong): Report {
  const voiceMax = voiceLimit(song);
  let voices = 0;
  let notes = 0;
  let missing = 0;
  let tooHigh = 0;
  let firstMissing = "";
  for (const [index, pattern] of song.patterns.entries()) {
    for (const note of pattern.notes) {
      notes++;
      voices = Math.max(voices, note.c + 1);
      if (note.n === NOTE_CUT) continue;
      const sound = song.sounds[note.s];
      if (!sound) {
        missing++;
        firstMissing = firstMissing || `pattern ${index + 1}, row ${note.r}, voice ${note.c + 1}`;
      } else if (note.n > maxNoteFor(sound.rate)) tooHigh++;
    }
  }
  const finals = song.sounds.map(finalizeSound);
  const bytes = finals.reduce((sum, sound) => sum + brrBytes(sound.data.length), 0);
  const empty = finals.filter((sound) => !sound.data.length).length;
  const free = song.mode === "free";
  const checks: Check[] = [];
  const add = (check: Check): void => void checks.push(check);

  // the same bars in both modes, only what going over means differs: snes mode locks the export, free mode warns
  const voiceState = meterState(voices, voiceMax, song.mode, 1);
  add({
    id: "voices",
    state: voiceState,
    title: "Voices",
    detail: `Using ${voices} of ${voiceMax}.`,
    fix: voiceState === "ok" ? "" : free
      ? `The SNES only has ${SNES_LIMITS.voices} voices${song.sfx ? ", and you kept 2 for game sound effects" : ""}, so this will not play on a real one.`
      : `The SNES has ${SNES_LIMITS.voices} voices${song.sfx ? ", and you kept 2 for game sound effects" : ""}. Move or delete the notes in the extra voices.`
  });
  const soundState = meterState(song.sounds.length, SNES_LIMITS.sounds, song.mode);
  add({
    id: "sounds",
    state: soundState,
    title: "Sounds",
    detail: `Using ${song.sounds.length} of ${SNES_LIMITS.sounds}.`,
    fix: soundState === "bad" ? "Remove sounds you do not use." : soundState === "warn" && song.sounds.length > SNES_LIMITS.sounds ? `The SNES only takes ${SNES_LIMITS.sounds} sounds.` : ""
  });
  const byteState = meterState(bytes, SNES_BYTES, song.mode);
  add({
    id: "memory",
    state: byteState,
    title: "Sample memory",
    detail: `${kb(bytes)} of ${kb(SNES_BYTES)} KB after SNES compression.`,
    fix: bytes > SNES_BYTES ? "Shorten sounds or record them at a lower quality in the Sounds tab. Sounds you do not use still count."
      : byteState === "warn" ? "Getting close. The game's own sound effects and other music share the same memory." : ""
  });
  add({
    id: "pitch",
    state: tooHigh ? "warn" : "ok",
    title: "Pitch",
    detail: tooHigh ? `${tooHigh} note${tooHigh === 1 ? " is" : "s are"} above the 128 kHz playback limit.` : "No note goes above the 128 kHz playback limit.",
    fix: tooHigh ? free ? "Free mode leaves them, but the SNES cannot play them." : "They are lowered to the highest allowed key when you export." : ""
  });
  add({
    id: "instruments",
    state: missing ? "bad" : "ok",
    title: "Sounds in use",
    detail: missing ? `${missing} note${missing === 1 ? " has" : "s have"} no sound (first one: ${firstMissing}).` : "Every note points at a sound that exists.",
    fix: missing ? "Give those notes a sound, or clear them. The SNES driver breaks on a note with no sound." : "",
    fatal: true
  });
  if (empty) add({ id: "empty", state: "bad", title: "Empty sounds", detail: `${empty} sound${empty === 1 ? " has" : "s have"} no audio left after trimming.`, fix: "Widen the trim or remove the sound.", fatal: true });
  add({
    id: "format",
    state: "ok",
    title: "File format",
    detail: "Impulse Tracker, uncompressed samples, one instrument per sound.",
    fix: "If you open the file in OpenMPT and save it again, turn off its IT sample compression first, the converter cannot read compressed samples."
  });
  if (!notes) add({ id: "silent", state: "warn", title: "No notes yet", detail: "The song is silent.", fix: "Tap a square on the Notes tab to add one." });
  const blocked = checks.some((check) => check.state === "bad" && (!free || check.fatal));
  return { mode: song.mode, voices, voiceMax, sounds: song.sounds.length, soundMax: SNES_LIMITS.sounds, bytes, byteMax: SNES_BYTES, notes, checks, blocked };
}

export function summaryOf(song: TrackerSong): TrackerSummary {
  const report = analyse(song);
  return { id: song.id, title: song.title, mode: song.mode, updatedAt: song.updatedAt, voices: report.voices, sounds: report.sounds, bytes: report.bytes, blocked: report.blocked };
}

// the notes as the file will have them: keys above a sound's limit come down to the highest allowed key
export function playableNote(song: TrackerSong, note: TrackerNote): TrackerNote {
  if (song.mode !== "snes" || note.n === NOTE_CUT) return note;
  const sound = song.sounds[note.s];
  return sound && note.n > maxNoteFor(sound.rate) ? { ...note, n: maxNoteFor(sound.rate) } : note;
}
